/**
 * Hook to determine whether to show mobile navigation
 * Only shows on actual mobile devices or small viewports
 */

import { useState, useEffect } from 'react';
import { isMobileDevice, isMobileViewport } from '../../../utils/deviceDetection';

export const useNavigationVisibility = () => {
  // Initialize with the correct values immediately
  // Use a safe check in case we're in SSR environment
  const [shouldShowMobileNav, setShouldShowMobileNav] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Check initial visibility on mount
    const isMobileDeviceResult = isMobileDevice();
    const isMobileViewportResult = isMobileViewport();
    const shouldShow = isMobileDeviceResult || isMobileViewportResult;
    return shouldShow;
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isMobileDevice();
  });

  useEffect(() => {
    const checkVisibility = () => {
      // Check if it's an actual mobile device
      const isActualMobile = isMobileDevice();
      setIsMobile(isActualMobile);
      
      // Show mobile nav only if:
      // 1. It's an actual mobile device, OR
      // 2. The viewport is mobile-sized (< 768px)
      const isMobileViewportResult = isMobileViewport();
      const showNav = isActualMobile || isMobileViewportResult;
      setShouldShowMobileNav(showNav);
    };

    // Check on mount (in case initial state needs update)
    checkVisibility();

    // Re-check on window resize for responsive behavior
    const handleResize = () => {
      checkVisibility();
    };

    window.addEventListener('resize', handleResize);
    
    // Also check on orientation change
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return {
    shouldShowMobileNav,
    isMobile,
    isDesktop: !isMobile
  };
};